import { defineStore } from 'pinia';
import { useErrorHandling } from 'src/core/composables/useErrorHandling';
import { useApiStore } from 'src/core/stores/api';
import { EnumType, EnumWithHintType, FormatedEnum } from 'src/core/types/enum';
import { ResponseParcelEnums } from 'src/core/types/enum/parcel';

type ParcelEnumsStore = {
  enums: ResponseParcelEnums;
  formatedEnums: FormatedEnum[];
};

/**
 * A store for managing parcels enums data.
 */
export const useParcelEnumsStore = defineStore('parcelEnums', {
  state: () =>
    ({
      enums: {} as ResponseParcelEnums,
      formatedEnums: [] as FormatedEnum[],
    } as ParcelEnumsStore),

  getters: {
    /**
     * Get the formatted value of an enum.
     * @param enumVal - The enum value to format.
     * @returns The formatted enum value.
     */
    getFormatedEnumValue: (state) => (enumVal: string) => {
      if (!enumVal || enumVal == '') return enumVal;
      return (
        state.formatedEnums.find(
          (enumValue: FormatedEnum) => enumValue[enumVal]
        )?.[enumVal] || enumVal
      );
    },

    /**
     * Get the enum instance based on the enum value.
     * @param enumVal - The enum value to search for.
     * @returns The enum value if found, undefined otherwise.
     */
    getEnumInstance: (state) => (enumVal: string) => {
      let foundEnum: EnumType | undefined;
      Object.keys(state.enums).forEach((key: string) => {
        if (foundEnum) return;
        foundEnum = state.enums[key as keyof typeof state.enums].find(
          (enumValue) => enumValue.value === enumVal
        );
      });
      return foundEnum;
    },

    getVarietyEnumInstance: (state) => (enumVal: string) => {
      let foundEnum: EnumType | undefined;
      Object.keys(state.enums.variety).forEach((key: string) => {
        if (foundEnum) return;
        foundEnum = state.enums.variety[
          key as keyof typeof state.enums.variety
        ].find((enumValue) => enumValue.value === enumVal);
      });
      return foundEnum;
    },

    getDestinationEnumInstance: (state) => (enumVal: string) => {
      let foundEnum: EnumWithHintType | undefined;
      Object.keys(state.enums).forEach((key: string) => {
        if (foundEnum) return;
        foundEnum = state.enums[key as keyof typeof state.enums].find(
          (enumValue) => enumValue.value === enumVal
        );
      });
      return foundEnum;
    },
    getHScodeEnumInstance: (state) => (enumVal: string) => {
      let foundEnum: EnumWithHintType | undefined;
      Object.keys(state.enums.hs_codes ? state.enums.hs_codes : {}).forEach(
        (key: string) => {
          if (foundEnum) return;
          foundEnum = state.enums.hs_codes[
            key as keyof typeof state.enums.hs_codes
          ].find((enumValue) => enumValue.value === enumVal);
        }
      );
      return foundEnum;
    },
  },

  actions: {
    /**
     * Get enums information.
     */
    async getEnums() {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      try {
        const responseEnums = await apiStore.service.get<ResponseParcelEnums>(
          '/parcel/enums'
        );
        this.formatedEnums = [];
        this.enums = responseEnums.data;
        Object.keys(responseEnums.data).forEach((key: string) => {
          if (key != 'hs_codes' && key != 'variety') {
            responseEnums.data[key as keyof ResponseParcelEnums].forEach(
              (enumValue: EnumType) => {
                this.formatedEnums.push({
                  [enumValue.value]: enumValue.label,
                });
              }
            );
          }
        });
      } catch (e) {
        handleError('Enums error', e);
      }
    },
  },
});
